import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import MDBox from '../components/MDBox';
import MDTypography from '../components/MDTypography';
import { useTheme } from '@mui/material/styles';
import { usePeriod } from '../components/PeriodContext';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { db, collection, query, getDocs } from '../firebaseConfig';

Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNumber;
};

const TeamMostProfitableSymbols = ({ userID, tradeAccountID }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [trades, setTrades] = useState([]);
  const [mostProfitableSymbol, setMostProfitableSymbol] = useState('N/A');
  const [mostFrequentSymbol, setMostFrequentSymbol] = useState('N/A');
  const [mostLossSymbol, setMostLossSymbol] = useState('N/A');
  const [mostFrequentLossSymbol, setMostFrequentLossSymbol] = useState('N/A');
  const [bestLongSymbol, setBestLongSymbol] = useState('N/A');
  const [bestShortSymbol, setBestShortSymbol] = useState('N/A');
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        setLoading(true);
        const tradesQuery = query(collection(db, `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`));
        const tradesDocs = await getDocs(tradesQuery);
        const tradesData = tradesDocs.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date ? new Date(doc.data().date) : new Date(),
        }));
        console.log('TeamMostProfitableSymbols: Fetched trades for userID:', userID, 'tradeAccountID:', tradeAccountID, tradesData);
        setTrades(tradesData);
      } catch (error) {
        console.error('TeamMostProfitableSymbols: Error fetching trades:', error);
        setTrades([]);
      } finally {
        setLoading(false);
      }
    };

    if (userID && tradeAccountID) {
      fetchTrades();
    }
  }, [userID, tradeAccountID]);

  useEffect(() => {
    const filterTrades = () => {
      let maxProfit = -Infinity;
      let maxProfitSymbol = 'N/A';
      let symbolFrequencyMap = {};
      let maxFrequency = 0;
      let mostFrequentSymbol = 'N/A';
      let maxLoss = Infinity;
      let maxLossSymbol = 'N/A';
      let symbolLossFrequencyMap = {};
      let maxLossFrequency = 0;
      let mostFrequentLossSymbol = 'N/A';
      let bestLongReturn = -Infinity;
      let bestLongSymbolName = 'N/A';
      let bestShortReturn = -Infinity;
      let bestShortSymbolName = 'N/A';

      trades
        .filter((entry) => entry.status === (showMissedTrades ? 'Missed' : 'Closed'))
        .forEach((entry) => {
          const entryDate = new Date(entry.date);
          const matchesPeriod = () => {
            if (selectedPeriod === 'today') {
              return entryDate.toDateString() === new Date().toDateString();
            } else if (selectedPeriod === 'this-week') {
              return entryDate.getWeek() === new Date().getWeek() && entryDate.getFullYear() === new Date().getFullYear();
            } else if (selectedPeriod === 'this-month') {
              return entryDate.getMonth() === new Date().getMonth() && entryDate.getFullYear() === new Date().getFullYear();
            } else if (selectedPeriod === 'this-year') {
              return entryDate.getFullYear() === new Date().getFullYear();
            } else if (selectedPeriod === 'pick-month') {
              return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
            } else if (selectedPeriod === 'pick-year') {
              return entryDate.getFullYear() === selectedYear;
            } else if (selectedPeriod === 'pick-month-year') {
              return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
            } else {
              return true; // all-time
            }
          };

          const matchesTag = selectedTag === 'all' || (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));

          if (matchesPeriod() && matchesTag) {
            const returns = parseFloat(entry.returns);
            const symbol = entry.symbol || 'N/A';
            const positionSide = entry.shortLong;

            // Most profitable symbol
            if (returns > maxProfit) {
              maxProfit = returns;
              maxProfitSymbol = symbol;
            }

            // Track best performing long/short positions
            if (positionSide === 'Long' && returns > bestLongReturn) {
              bestLongReturn = returns;
              bestLongSymbolName = symbol;
            }
            if (positionSide === 'Short' && returns > bestShortReturn) {
              bestShortReturn = returns;
              bestShortSymbolName = symbol;
            }

            // Most frequent symbol (winning trades)
            if (returns > 0) {
              symbolFrequencyMap[symbol] = (symbolFrequencyMap[symbol] || 0) + 1;
              if (symbolFrequencyMap[symbol] > maxFrequency) {
                maxFrequency = symbolFrequencyMap[symbol];
                mostFrequentSymbol = symbol;
              }
            }

            // Most loss symbol
            if (returns < 0 && returns < maxLoss) {
              maxLoss = returns;
              maxLossSymbol = symbol;
            }

            // Most frequent loss symbol
            if (returns < 0) {
              symbolLossFrequencyMap[symbol] = (symbolLossFrequencyMap[symbol] || 0) + 1;
              if (symbolLossFrequencyMap[symbol] > maxLossFrequency) {
                maxLossFrequency = symbolLossFrequencyMap[symbol];
                mostFrequentLossSymbol = symbol;
              }
            }
          }
        });

      setMostProfitableSymbol(maxProfitSymbol);
      setMostFrequentSymbol(mostFrequentSymbol);
      setMostLossSymbol(maxLossSymbol);
      setMostFrequentLossSymbol(mostFrequentLossSymbol);
      setBestLongSymbol(bestLongSymbolName);
      setBestShortSymbol(bestShortSymbolName);
    };

    filterTrades();
  }, [trades, selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag]);

  const tooltipContent = `The Trading Performance Summary highlights: Most Profitable trade, Frequently Profitable symbols, Best Long/Short positions, Biggest Losses, and Frequent Loss patterns, based on ${showMissedTrades ? 'missed' : 'closed'} trades filtered by period and tags.`;

  if (loading) {
    return (
      <Card style={{ minHeight: '260px' }}>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
          <MDTypography variant="caption" color="text">
            Loading trades...
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  return (
    <Card style={{ minHeight: '260px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <CurrencyExchangeIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Most Profitable Symbols {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.background.paper,
                    },
                  },
                },
              }}
            >
              <InfoIcon
                sx={{
                  ml: 1,
                  fontSize: '1rem',
                  color: iconColor,
                  cursor: 'help',
                }}
              />
            </Tooltip>
            <MDTypography variant="h4" fontFamily="inherit" style={{ visibility: 'hidden' }}>
              0
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox pb={2} px={2}>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Most Profitable:{' '}
            <MDTypography component="span" variant="button" fontWeight="bold" color="success" fontFamily="inherit">
              {mostProfitableSymbol}
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Frequently Profitable:{' '}
            <MDTypography component="span" variant="button" fontWeight="bold" color="success" fontFamily="inherit">
              {mostFrequentSymbol}
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Biggest Loss:{' '}
            <MDTypography component="span" variant="button" fontWeight="bold" color="error" fontFamily="inherit">
              {mostLossSymbol}
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Frequent Loss:{' '}
            <MDTypography component="span" variant="button" fontWeight="bold" color="error" fontFamily="inherit">
              {mostFrequentLossSymbol}
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Best Long:{' '}
            <MDTypography component="span" variant="button" fontWeight="bold" color="success" fontFamily="inherit">
              {bestLongSymbol}
            </MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Best Short:{' '}
            <MDTypography component="span" variant="button" fontWeight="bold" color="success" fontFamily="inherit">
              {bestShortSymbol}
            </MDTypography>
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
};

TeamMostProfitableSymbols.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string.isRequired,
};

export default TeamMostProfitableSymbols;